import React, { useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { patchCollectDataCollection } from '../../services/api/collectDataService';

export type ReminderFrequencyOption = '6h' | '12h' | '24h' | '48h' | '72h' | '168h';

type Props = {
    collectionId: string | undefined;
    accessToken: string | undefined;
    collection: Record<string, unknown> | null;
    t: TFunction;
    onSaved: () => Promise<void> | void;
};

function toDatetimeLocalValue(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CollectWorkspaceSettingsModal: React.FC<Props> = ({ collectionId, accessToken, collection, t, onSaved }) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const closeBtnRef = useRef<HTMLButtonElement>(null);
    const [interviewEndLocal, setInterviewEndLocal] = useState('');
    const [reminderFrequency, setReminderFrequency] = useState<ReminderFrequencyOption>('6h');
    const [recipientsCanInvite, setRecipientsCanInvite] = useState(false);
    const [anonymousResponses, setAnonymousResponses] = useState(false);
    const [saving, setSaving] = useState(false);
    const [localErr, setLocalErr] = useState<string | null>(null);

    useEffect(() => {
        const iso = collection?.interview_end_at;
        setInterviewEndLocal(typeof iso === 'string' ? toDatetimeLocalValue(iso) : '');
        const rf = collection?.reminder_frequency;
        const allowed: ReminderFrequencyOption[] = ['6h', '12h', '24h', '48h', '72h', '168h'];
        setReminderFrequency(
            typeof rf === 'string' && (allowed as string[]).includes(rf) ? (rf as ReminderFrequencyOption) : '6h',
        );
        setRecipientsCanInvite(Boolean(collection?.recipients_can_invite));
        setAnonymousResponses(Boolean(collection?.anonymous_responses));
    }, [collection]);

    const save = async () => {
        if (!collectionId || !accessToken) return;
        setSaving(true);
        setLocalErr(null);
        let interview_end_at: string | null = null;
        if (interviewEndLocal.trim()) {
            const d = new Date(interviewEndLocal);
            if (Number.isNaN(d.getTime())) {
                setLocalErr(t('form.collectWorkspace.settingsInvalidDate', 'Invalid date'));
                setSaving(false);
                return;
            }
            interview_end_at = d.toISOString();
        }
        const res = await patchCollectDataCollection(
            collectionId,
            {
                interview_end_at,
                reminder_frequency: reminderFrequency,
                recipients_can_invite: recipientsCanInvite,
                anonymous_responses: anonymousResponses,
            },
            accessToken,
        );
        setSaving(false);
        const inner = res.body?.status_code;
        const ok = res.status === 200 && typeof inner === 'number' && inner >= 200 && inner < 300;
        if (!ok) {
            setLocalErr(res.body?.description || t('words.error'));
            return;
        }
        closeBtnRef.current?.click();
        await onSaved();
    };

    const tk = 'form.collectWorkspace.settingsModal';

    return (
        <div className="modal fade" id="collectWorkspaceSettings" tabIndex={-1} ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content bg-dark text-light border-secondary">
                    <div className="modal-header border-secondary">
                        <h5 className="modal-title">{t(`${tk}.title`, 'Collection settings')}</h5>
                        <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" />
                    </div>
                    <div className="modal-body">
                        {localErr ? <div className="alert alert-danger py-2 small">{localErr}</div> : null}
                        <div className="mb-3">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <label className="form-label small text-light mb-0" htmlFor="cw-settings-end">
                                    {t(`${tk}.interviewEnd`, 'Interview end date')}
                                </label>
                                {interviewEndLocal ? (
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm text-light text-opacity-75 text-decoration-none p-0"
                                        onClick={() => setInterviewEndLocal('')}
                                    >
                                        {t(`${tk}.interviewEndClear`, 'Clear date')}
                                    </button>
                                ) : null}
                            </div>
                            <input
                                id="cw-settings-end"
                                type="datetime-local"
                                className="form-control bg-dark text-light border-secondary mt-1"
                                value={interviewEndLocal}
                                onChange={(e) => setInterviewEndLocal(e.target.value)}
                            />
                            <div className="form-text small text-light text-opacity-75">
                                {t(`${tk}.interviewEndHint`, 'Optional deadline for completing interviews in this collection.')}
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label small text-light" htmlFor="cw-settings-reminder">
                                {t(`${tk}.reminderFrequency`, 'Reminder email frequency')}
                            </label>
                            <select
                                id="cw-settings-reminder"
                                className="form-select bg-dark text-light border-secondary"
                                value={reminderFrequency}
                                onChange={(e) => setReminderFrequency(e.target.value as ReminderFrequencyOption)}
                            >
                                <option value="6h">{t(`${tk}.reminder6h`, '6 hours after invite')}</option>
                                <option value="12h">{t(`${tk}.reminder12h`, '12 hours after invite')}</option>
                                <option value="24h">{t(`${tk}.reminder24h`, '24 hours after invite')}</option>
                                <option value="48h">{t(`${tk}.reminder48h`, '48 hours after invite')}</option>
                                <option value="72h">{t(`${tk}.reminder72h`, '72 hours after invite')}</option>
                                <option value="168h">{t(`${tk}.reminder168h`, '7 days after invite')}</option>
                            </select>
                        </div>
                        <label className="collect-ws-settings-switch-label mb-3">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={anonymousResponses}
                                id="cw-settings-anonymous"
                                className={`collect-ws-settings-switch${anonymousResponses ? ' is-on' : ''}`}
                                onClick={() => setAnonymousResponses((v) => !v)}
                            >
                                <span className="collect-ws-settings-switch__thumb" aria-hidden />
                            </button>
                            <span className="collect-ws-settings-switch-label__text">
                                <span className="d-block fw-medium">
                                    {t(`${tk}.anonymousResponses`, 'Anonymous responses')}
                                </span>
                                <span className="d-block small text-light text-opacity-75 mt-1">
                                    {t(
                                        `${tk}.anonymousResponsesHint`,
                                        'Contributors are shown to you as fictitious names (animals or stars). You cannot map a name to a person.',
                                    )}
                                </span>
                            </span>
                        </label>
                        <label className="collect-ws-settings-switch-label mb-2">
                            <button
                                type="button"
                                role="switch"
                                aria-checked={recipientsCanInvite}
                                id="cw-settings-invite"
                                className={`collect-ws-settings-switch${recipientsCanInvite ? ' is-on' : ''}`}
                                onClick={() => setRecipientsCanInvite((v) => !v)}
                            >
                                <span className="collect-ws-settings-switch__thumb" aria-hidden />
                            </button>
                            <span className="collect-ws-settings-switch-label__text">
                                {t(`${tk}.recipientsCanInvite`, 'Recipients can invite other recipients')}
                            </span>
                        </label>
                    </div>
                    <div className="modal-footer border-secondary">
                        <button ref={closeBtnRef} type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                            {t('words.cancel', 'Cancel')}
                        </button>
                        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
                            {saving ? t('words.loading') : t('words.save', 'Save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollectWorkspaceSettingsModal;
